import { DeleteOutlined, SettingOutlined } from '@ant-design/icons';
import { Button, message, Modal, Space, Select, Tag } from 'antd';
import * as Component from 'components';
import { seriesService } from 'content/service';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { SeriesInterface, SeriesData } from '../../type/seriesType';
import { LanguageCode, SUPPORTED_LANGUAGES } from '../../../../shared/constants/i18n/languages';
import {
  getTranslation,
  getSeriesSupportedLanguages,
} from '../../../../shared/services/i18n/i18nService';
import SeriesBulkUpdateButton from '../components/main/SeriesBulkUpdateButton';

export default function Series(): React.JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams({
    page: '1',
    page_size: '100',
    status: 'draft,published',
  });
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [selectedList, setSelectedList] = useState<number[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageCode>('ko');
  const [_seriesData, setSeriesData] = useState<SeriesData[]>([]);
  const navigate = useNavigate();

  const columns = [
    {
      title: 'POSTER',
      dataIndex: 'poster',
      key: 'poster',
      width: '15%',
      render: (_text: string, record: SeriesInterface): JSX.Element => (
        <img
          style={{ marginLeft: '10px', width: '100%', height: 'auto' }}
          src={record.poster?.file}
          alt=""
        />
      ),
    },
    {
      title: 'TITLE',
      dataIndex: 'title',
      key: 'title',
      width: '25%',
      sorter: (a: SeriesInterface, b: SeriesInterface): number => a.title.localeCompare(b.title),
      render: (text: string, record: SeriesData): JSX.Element => {
        // i18n 필드에서 선택된 언어의 제목 가져오기
        const displayTitle = getTranslation(record.i18n_title, selectedLanguage) || text;
        const supportedLanguages = getSeriesSupportedLanguages(record as any);
        const hasTranslation = record.i18n_title?.[selectedLanguage];

        return (
          <div>
            <a
              href="#"
              className="season-title"
              onClick={() => window.open(`/series/edit/${record.id}`, '_blank')}
              style={{
                color: hasTranslation ? '#1890ff' : '#999',
                fontStyle: hasTranslation ? 'normal' : 'italic',
              }}
            >
              {displayTitle}
            </a>
            <div style={{ marginTop: '4px' }}>
              {supportedLanguages.map(lang => (
                <Tag
                  key={lang}
                  color={lang === selectedLanguage ? 'blue' : 'default'}
                  style={{ fontSize: '10px' }}
                >
                  {SUPPORTED_LANGUAGES[lang]?.flag} {SUPPORTED_LANGUAGES[lang]?.label}
                </Tag>
              ))}
              {supportedLanguages.length === 0 && (
                <Tag color="red" style={{ fontSize: '10px' }}>
                  번역 없음
                </Tag>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: 'SLUG',
      dataIndex: 'slug',
      key: 'slug',
      width: '10%',
      sorter: (a: SeriesInterface, b: SeriesInterface): number => a.slug.localeCompare(b.slug),
      render: (text: string, record: SeriesInterface): JSX.Element => (
        <div
          className="season-title"
          onClick={() => window.open(`/series/edit/${record.id}`, '_blank')}
        >
          <div
            className="season-title"
            style={{
              width: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {text}
          </div>
        </div>
      ),
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      width: '5%',
      filters: [
        {
          text: 'Draft',
          value: 'draft',
        },
        {
          text: 'Published',
          value: 'published',
        },
        {
          text: 'Unpublished',
          value: 'unpublished',
        },
      ],
      filteredValue: searchParams.get('status') ? searchParams.get('status')?.split(',') : null,
      render: (text: string): string => text,
    },
    {
      title: 'DUBBING MODE',
      dataIndex: 'dubbing_mode',
      key: 'dubbing_mode',
      width: '5%',
      filters: [
        {
          text: 'Inherit',
          value: 'inherit',
        },
        {
          text: 'Enabled',
          value: 'enabled',
        },
        {
          text: 'Disabled',
          value: 'disabled',
        },
      ],
      filteredValue: searchParams.get('dubbing_modes')
        ? searchParams.get('dubbing_modes')?.split(',')
        : null,
      render: (text: string): string => text,
    },
    {
      title: 'ACTIONS',
      key: 'actions',
      dataIndex: 'actions',
      render: (_text: string, record: SeriesInterface): JSX.Element => (
        <Space>
          <Button
            icon={<SettingOutlined />}
            onClick={() => window.open(`/series/edit/${record.id}`, '_blank')}
            className="action-btn"
          />
          <Button
            icon={<DeleteOutlined />}
            onClick={() => {
              if (record.seasons && record.seasons.length > 0) {
                message.warning(
                  `이 시리즈에는 ${record.seasons.length}개의 시즌이 포함되어 있어 삭제할 수 없습니다.`
                );
                return;
              }

              Modal.confirm({
                title: '정말 삭제하시겠습니까?',
                content: '삭제하면 복구할 수 없습니다.',
                okText: '삭제',
                okType: 'danger',
                cancelText: '취소',
                onOk: async () => {
                  try {
                    await seriesService.delete(record.id);
                    message.success('삭제가 완료되었습니다.');
                    window.location.href = '/series/';
                  } catch {
                    message.error('삭제 중 오류가 발생했습니다.');
                  }
                },
              });
            }}
            className={record.is_deletable ? 'action-btn' : 'delete-btn'}
          />
        </Space>
      ),
    },
  ];

  const fetchSeriesTableData = useCallback(
    async (params: URLSearchParams): Promise<{ data: SeriesData[]; count: number }> => {
      try {
        const seriesList = await seriesService.fetchSeriesListWithI18n(params);
        setSeriesData(seriesList.results);
        return { data: seriesList.results, count: seriesList.count };
      } catch (error) {
        console.error('Failed to fetch series with i18n:', error);
        const seriesList = await seriesService.fetch(params);
        return { data: seriesList.results as SeriesData[], count: seriesList.count };
      }
    },
    [searchParams, selectedLanguage]
  );

  useEffect(() => {
    setRefreshCounter(prev => prev + 1);
  }, [searchParams]);

  return (
    <Component.BasePageComponent>
      <Component.TableComponent<SeriesData>
        key={refreshCounter}
        name="Series"
        searchParams={searchParams}
        setSearchParams={setSearchParams}
        dataFetch={fetchSeriesTableData}
        columns={columns}
        actions={{
          onSelectionChange: setSelectedList,
          onAddClick: () => navigate('/series/add'),
        }}
        additionalButton={[SeriesBulkUpdateButton({ selectedList })]}
      />
    </Component.BasePageComponent>
  );
}
